import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import { CVPoolService, type CVFormData } from '~/services/CVPoolService';

/**
 * CV havuzu için composable
 */
export function useCV() {
  const toast = useToast();
  const cvService = CVPoolService.getInstance();
  
  // Form durumu
  const form = ref<CVFormData & { agreement?: boolean, otherProfession?: string }>({
    name: '',
    email: '',
    profession: '',
    job_type: 'job', // Varsayılan olarak iş başvurusu
    cv: null,
    agreement: false
  });
  
  // İşlem durumları
  const isLoading = ref<boolean>(false);
  const isSuccess = ref<boolean>(false);
  const error = ref<string | null>(null);
  
  // Form doğrulama mantığı
  const validateForm = (): boolean => {
    if (!form.value.name) {
      error.value = 'İsim alanı zorunludur';
      return false;
    }
    
    if (!form.value.email) {
      error.value = 'E-posta alanı zorunludur';
      return false;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
      error.value = 'Geçerli bir e-posta adresi giriniz';
      return false;
    }
    
    if (!form.value.profession) {
      error.value = 'Meslek alanı zorunludur';
      return false;
    }
    
    if (!form.value.cv) {
      error.value = 'CV yüklemeniz zorunludur';
      return false;
    }
    
    if (form.value.profession === 'other' && !form.value.otherProfession) {
      error.value = 'Diğer meslek alanı zorunludur';
      return false;
    }
    
    if (!form.value.agreement) {
      error.value = 'Kullanım koşullarını kabul etmelisiniz';
      return false;
    }
    
    return true;
  };
  
  // CV gönderme işlemi
  const submitCV = async (): Promise<boolean> => {
    // Gerekli kontroller
    if (!validateForm()) {
      toast.error(error.value);
      return false;
    }
    
    // İşlem başlatıldı
    isLoading.value = true;
    error.value = null;
    isSuccess.value = false;
    
    try {
      // API isteği gönder
      const response = await cvService.submitCV(form.value);
      
      // Başarılı yanıt
      if (response.status === '200' || response.status === '201' || response.status === 'success') {
        isSuccess.value = true;
        toast.success('CV başvurunuz başarıyla gönderildi.');
        
        // Formu temizle
        resetForm();
        return true;
      } else {
        error.value = response.message || 'CV gönderilirken bir hata oluştu.';
        toast.error(error.value);
        return false;
      }
    } catch (err) {
      error.value = 'CV gönderilirken bir hata oluştu.';
      toast.error(error.value);
      console.error('CV gönderme hatası:', err);
      return false;
    } finally {
      isLoading.value = false;
    }
  };
  
  // Formu temizle
  const resetForm = () => {
    form.value = {
      name: '',
      email: '',
      profession: '',
      job_type: 'job',
      cv: null,
      agreement: false
    };
  };
  
  // Dosya yükleme
  const setFile = (file: File | null) => {
    form.value.cv = file;
  };
  
  // İş tipi belirleme (iş veya staj)
  const setJobType = (type: 'job' | 'internship') => {
    form.value.job_type = type;
  };
  
  return {
    form,
    isLoading,
    isSuccess,
    error,
    submitCV,
    resetForm,
    setFile,
    setJobType
  };
} 